/**
 * The creatives the client may ask for, as the prototype's left column
 * (`poc:.pf-modal__creative-list`): a row each, naming what booking that person
 * adds to the order.
 *
 * It is a list of rows rather than a table because it sits beside a calendar in a
 * third of the width. The comparison it supports: who, and what their travel costs.
 *
 * Shown only while "choose your creative" is on. With it off, Backbone assigns
 * whoever fits and no travel fee applies, which is why the toggle carries that
 * warning rather than this list.
 */

import React from 'react'
import { Link, Stack, Typography } from '@mui/material'
import ReactLoading from 'react-loading'
import { useTranslation } from 'react-i18next'

import { GRAY_500, GRAY_700 } from 'constants/styling/theme'
import { MUICheckCard } from 'components/common/MUICards'
import { formatPrice } from 'utils/price'
import type { RevampCreative } from 'dataQueries/purchaseFlowRevampCreatives.query'

interface Props {
  creatives: ReadonlyArray<RevampCreative>
  /** Selected creative's id, or undefined while Backbone is to assign one. */
  value?: string
  onSelect: (id: string) => void
  isLoading?: boolean
}

export const ScheduleCreativeList: React.FC<Props> = ({ creatives, value, onSelect, isLoading = false }) => {
  const { t } = useTranslation(['purchase_flow_revamp'])

  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" gap={0.8} sx={{ py: 1.2 }}>
        <ReactLoading type="spin" color={GRAY_500} width="1.6rem" height="1.6rem" />
        <Typography variant="text-sm" color={GRAY_700}>
          {t('purchase_flow_revamp:instructions.ct_booking.loading')}
        </Typography>
      </Stack>
    )
  }

  if (creatives.length === 0) {
    return (
      <Typography variant="text-sm" color={GRAY_700} sx={{ py: 1.2 }}>
        {t('purchase_flow_revamp:instructions.ct_booking.empty')}
      </Typography>
    )
  }

  return (
    <Stack
      gap={0.8}
      role="radiogroup"
      aria-label={t('purchase_flow_revamp:instructions.schedule.choose_creative_toggle')}
      data-cy="pf-revamp-schedule-creative-list"
      data-pf-gap="favourite_creative_missing"
      // Long lists scroll inside the column rather than stretching the panel
      // past the calendar beside it.
      sx={{ maxHeight: '32rem', overflowY: 'auto', pr: 0.4 }}
    >
      {creatives.map((creative) => {
        const selected = creative.id === value
        const fee = creative.travelCost

        return (
          <MUICheckCard
            key={creative.id}
            isRadio
            showSelectElement={false}
            selectionStyle="ring"
            checked={selected}
            onCheck={() => onSelect(creative.id)}
            data-cy={`pf-revamp-schedule-creative-${creative.id}`}
            title={creative.fullName}
            // The fee, or its absence, said out loud either way: "no transport fee" is the reason
            // to pick one creative over another, so leaving it blank would hide half the comparison.
            description={
              <Stack component="span" gap={0.2}>
                <span>
                  {fee && fee.amount > 0
                    ? `+ ${formatPrice(fee.amount, fee.currency)} ${t('purchase_flow_revamp:instructions.ct_booking.travel_cost')}`
                    : t('purchase_flow_revamp:instructions.schedule.no_transport_fee')}
                </span>
                {creative.phoneNumber &&
                  <Link
                    href={`tel:${creative.phoneNumber.replace(/\s/g, '')}`}
                    // Calling must not also select the card.
                    onClick={(event) => event.stopPropagation()}
                    color="inherit"
                    data-cy={`pf-revamp-schedule-creative-phone-${creative.id}`}
                  >
                    {creative.phoneNumber}
                  </Link>
                }
              </Stack>
            }
          />
        )
      })}
    </Stack>
  )
}
